import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Sparkles, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { extractError } from "../lib/format";

const MAX_IMAGES = 5;
const MAX_MB = 10;
const OK_MIMES = ["image/jpeg", "image/png", "image/webp"];

const GRADE_LABEL = {
  excellent: "Excellent",
  good: "Good work",
  attempted: "Nice try",
};

export default function SubmitHomework({ lessonId, onSubmitted }) {
  const [files, setFiles] = useState([]); // {file, previewUrl}
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // submission object
  const [showTranscription, setShowTranscription] = useState(false);
  const inputRef = useRef(null);

  const previews = useMemo(() => files.map((f) => f.previewUrl), [files]);

  const acceptFiles = (list) => {
    const incoming = Array.from(list || []);
    const errs = [];
    const good = [];
    for (const f of incoming) {
      if (!OK_MIMES.includes(f.type)) {
        errs.push(`${f.name}: not a JPEG/PNG/WEBP`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        errs.push(`${f.name}: over ${MAX_MB} MB`);
        continue;
      }
      good.push({ file: f, previewUrl: URL.createObjectURL(f) });
    }
    if (errs.length) toast.error(errs.join(" · "));
    setFiles((cur) => {
      const combined = [...cur, ...good].slice(0, MAX_IMAGES);
      if (cur.length + good.length > MAX_IMAGES) {
        toast.error(`You can attach up to ${MAX_IMAGES} images per submission.`);
      }
      return combined;
    });
  };

  const removeAt = (idx) => {
    setFiles((cur) => {
      const copy = cur.slice();
      const [removed] = copy.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return copy;
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFiles(e.dataTransfer?.files);
  };

  const submit = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("images", f.file));
      const { data } = await api.post(
        `/submissions?lesson_id=${encodeURIComponent(lessonId)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      setResult(data.submission);
      // clear previews so parents can submit again
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
      onSubmitted && onSubmitted(data.submission);
      toast.success("Homework submitted!");
    } catch (e) {
      const status = e?.response?.status;
      const msg = extractError(e, "Could not submit homework.");
      if (status === 503) {
        toast.info(msg);
        // saved but ai unavailable — pretend soft result
        setResult({
          status: "needs_retry",
          ai_feedback: msg,
          ai_grade: null,
          ocr_text: "",
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const gradeChip = (g) => {
    if (!g) return null;
    return (
      <span className={`ftg-hw__chip ftg-hw__chip--${g}`} data-testid="hw-grade-chip">
        <Sparkles size={12} /> {GRADE_LABEL[g] || g}
      </span>
    );
  };

  return (
    <section className="ftg-hw" data-testid="hw-submit-section">
      <div className="ftg-hw__head">
        <div>
          <h3 className="ftg-hw__title">Submit your homework</h3>
          <p className="ftg-hw__sub">
            Snap a photo (or up to {MAX_IMAGES}) of your workbook page. We&apos;ll give you warm, specific feedback in a few seconds.
          </p>
        </div>
      </div>

      {!result && (
        <>
          <div
            className={`ftg-hw__drop${dragging ? " is-drag" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            data-testid="hw-dropzone"
          >
            <UploadCloud size={26} />
            <div>
              <strong>Drop images here</strong> or click to choose
              <div className="ftg-hw__hint">JPEG, PNG, or WEBP · up to {MAX_MB} MB each · max {MAX_IMAGES}</div>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              data-testid="hw-file-input"
              onChange={(e) => {
                acceptFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {!!files.length && (
            <div className="ftg-hw__previews" data-testid="hw-previews">
              {previews.map((src, i) => (
                <div key={i} className="ftg-hw__thumb">
                  <img src={src} alt={`Page ${i + 1}`} />
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="ftg-hw__thumb-x"
                    aria-label={`Remove page ${i + 1}`}
                    data-testid={`hw-remove-${i}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="ftg-hw__actions">
            <button
              type="button"
              onClick={submit}
              disabled={!files.length || busy}
              className="ftg-btn ftg-btn--primary"
              data-testid="hw-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="ftg-spin" /> Getting feedback…
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Submit for feedback
                </>
              )}
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="ftg-hw__card" data-testid="hw-result-card">
          <div className="ftg-hw__card-head">
            <h4 className="ftg-hw__card-title">Your feedback</h4>
            {gradeChip(result.ai_grade)}
          </div>
          <p className="ftg-hw__feedback" data-testid="hw-feedback-text">
            {result.ai_feedback || "Your submission is saved."}
          </p>
          {result.ocr_text && (
            <div className="ftg-hw__trans">
              <button
                type="button"
                className="ftg-hw__trans-toggle"
                onClick={() => setShowTranscription((v) => !v)}
                data-testid="hw-toggle-transcription"
              >
                {showTranscription ? (
                  <>
                    <ChevronUp size={14} /> Hide what we read
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} /> Show what we read from the page
                  </>
                )}
              </button>
              {showTranscription && (
                <div className="ftg-hw__trans-body" data-testid="hw-transcription">
                  {result.ocr_text}
                </div>
              )}
            </div>
          )}
          <div className="ftg-hw__actions" style={{ marginTop: 14 }}>
            <button
              type="button"
              className="ftg-btn ftg-btn--ghost"
              onClick={() => {
                setResult(null);
                setShowTranscription(false);
              }}
              data-testid="hw-another-btn"
            >
              Submit another
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
